import { FullStory } from '@fullstory/browser';
import { useOktaAuth } from '@okta/okta-react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { createContext, ReactElement, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { PageRoutes } from '../../shared/routes';
import { Config } from '../../shared/utils/config';
import { normalizeInternalEmail } from '../../shared/utils/utils';
import {
    getAccessToken,
    setAccessToken,
    setOktaAuthEmail,
} from '../../shared/utils/sessionStorageUtils';

export const AuthContext = createContext({
    error: '',
    setError: (_error: string) => {},
});

const AUTH_ROUTES: string[] = [PageRoutes.AUTH_ERROR, PageRoutes.LOGIN_CALLBACK];

export const AuthContextProvider = ({ children }: { children: ReactElement | ReactNode }) => {
    const oktaAuth = useOktaAuth();
    const authState = oktaAuth?.authState;
    const ldClient = useLDClient();

    const [error, setError] = useState('');
    const [isAuthReady, setIsAuthReady] = useState(() => !!getAccessToken());
    const hasIdentifiedFullStory = useRef(false);
    const hasIdentifiedLD = useRef(false);

    useEffect(() => {
        const accessToken = getAccessToken();

        if (authState && authState.isAuthenticated && authState.accessToken) {
            const authStateAccessToken = authState.accessToken.accessToken;
            const rawAuthEmail = authState.idToken?.claims?.email;
            const authEmail = rawAuthEmail ? normalizeInternalEmail(rawAuthEmail) : rawAuthEmail;

            if (!accessToken || accessToken !== authStateAccessToken) {
                setAccessToken(authStateAccessToken);
            }

            if (authEmail) {
                setOktaAuthEmail(authEmail);

                if (Config.FullStoryOrganizationId && !hasIdentifiedFullStory.current) {
                    try {
                        FullStory('setIdentity', { uid: authEmail });
                    } catch (err: unknown) {
                        console.error('FullStory setIdentity error: ', err);
                    }

                    try {
                        FullStory('setProperties', {
                            type: 'user',
                            properties: {
                                email: authEmail,
                                app: 'Review',
                                env: Config.Environment
                            },
                        });
                    } catch (err: unknown) {
                        console.error('FullStory setProperties error: ', err);
                    }

                    hasIdentifiedFullStory.current = true;
                }

                if (ldClient && !hasIdentifiedLD.current) {
                    ldClient.identify({ kind: 'user', key: authEmail, email: authEmail }).catch(() => {});
                    hasIdentifiedLD.current = true;
                }
            }

            setIsAuthReady(true);
        }
    }, [authState, ldClient]);

    if (!isAuthReady && !AUTH_ROUTES.includes(window.location.pathname)) {
        return null;
    }

    return (
        <AuthContext.Provider
            value={{
                error,
                setError,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
